import { useCallback, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ButtonRounded, Caption, H3, VerticalCard } from '@comeon/ui';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { selectLocale } from '~/components/brand/ducks/selectors';
import currencyFormatter from '../../../libs/currency-formatter.js';
import useContextualPathTransformer from '../../../hooks/use-contextual-path-transformer.js';
import {
	ROUTE_WE_SPIN_GAME_VIEW,
	ROUTE_WE_SPIN_GAME_ROOM_STREAMER,
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW
} from './constants/we-spin-route-constants.js';
import {
	SESSION_OFFLINE_RESULT,
	SESSION_OFFLINE,
	SESSION_ONLINE,
	REGISTRATION,
	RUNNING,
	ENDED,
	PENDING_START
} from './constants/we-spin-stream-states.js';

import { streamToGameRoomRouteEventList } from './utils/stream-to-game-room-route.js';
import StartTime from './components/start-time.js';
import WeSpinFlag from './we-spin-flag.js';
import {
	selectRunningEventForSession,
	selectNextBuyInEventForSession,
	selectIsSessionOnline
} from './ducks/we-spin-selectors.js';
import { FREE_WESPIN, getTagText } from './constants/we-spin-boost-constants.js';
import WeSpinSessionCardImage from './we-spin-session-card-image.js';
import {
	WE_SPIN_SESSION_CARD_DATA_AT,
	WE_SPIN_SESSION_CARD_COUNTDOWN_DATA_AT,
	WE_SPIN_SESSION_CARD_STREAMER_NAME_DATA_AT,
	WE_SPIN_SESSION_CARD_PRIMARY_BTN_DATA_AT
} from './constants/we-spin-data-at-attributes.js';
import { setCurrentStreamView } from './ducks/we-spin-duck.js';

const PositionedFigure = styled.figure`
	order: ${props => (props.order ? props.order : 'inherit')};
`;

const Button = memo(
	({
		onClick,
		balanceCapMet,
		buyInAmount,
		isJoined,
		sessionState,
		translations,
		noParticipants,
		dataAt,
		playerCapMet,
		isFreeWeSpin,
		streamView
	}) => {
		const isFullyBooked = (balanceCapMet || playerCapMet) && translations.WE_SPIN_LOBBY_CARD_BUTTON_FULLY_BOOKED;
		const buyIn =
			isJoined && !isFreeWeSpin && `${translations.WE_SPIN_LOBBY_CARD_BUTTON_YOUR_BUY_IN}: ${buyInAmount}`;

		const joinedFreeWeSpin = isJoined && isFreeWeSpin && `${translations.WE_SPIN_LOBBY_CARD_BUTTON_JOINED}`;

		const buyInFreeWeSpin =
			!isJoined &&
			isFreeWeSpin &&
			!playerCapMet &&
			streamView.state === PENDING_START &&
			`${translations.WE_SPIN_LOBBY_CARD_BUTTON_JOIN}`;

		let text = '';

		const joinText =
			noParticipants === 0
				? `${translations.WE_SPIN_LOBBY_CARD_BUTTON_JOIN_US}`
				: noParticipants > 1
				? `${translations.WE_SPIN_LOBBY_CARD_BUTTON_JOIN} ${noParticipants} ${translations.WE_SPIN_LOBBY_CARD_PLAYERS_TEXT}`
				: `${translations.WE_SPIN_LOBBY_CARD_BUTTON_JOIN} ${noParticipants} ${translations.WE_SPIN_LOBBY_CARD_PLAYERS_TEXT}`;
		switch (sessionState) {
			case SESSION_OFFLINE_RESULT:
				text = translations.WE_SPIN_LOBBY_CARD_BUTTON_VIEW_RESULT;
				break;
			case SESSION_ONLINE:
				text = translations.WE_SPIN_LOBBY_CARD_BUTTON_GO_TO_EVENT;
				break;
			default:
				text = buyIn || buyInFreeWeSpin || joinedFreeWeSpin || isFullyBooked || joinText;
		}

		return (
			<ButtonRounded className="btn btn--light u-width-100 btn--wespin" onClick={onClick} dataAt={dataAt}>
				{text}
			</ButtonRounded>
		);
	}
);

const WeSpinSessionCard = ({ session, translations, cssOrder }) => {
	const {
		estimatedStartTime,
		joined,
		balanceCapMet,
		buyInAmount,
		sessionState,
		noParticipants,
		streamViews,
		sessionRef,
		playerCapMet
	} = session;

	const isOnline = useSelector(selectIsSessionOnline(sessionRef));
	const locale = useSelector(selectLocale);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const runningStreamView = useSelector(selectRunningEventForSession(sessionRef));
	const nextUpcomingStreamView = useSelector(selectNextBuyInEventForSession(sessionRef));

	const streamView = runningStreamView || nextUpcomingStreamView || streamViews[0];
	const isFreeWeSpin = streamView.boost?.type === FREE_WESPIN;

	const transformPath = useContextualPathTransformer();
	const onClickLink = useCallback(
		e => {
			let link;
			if (!streamView.isPrivateStreamer) {
				link = !isOnline
					? transformPath(streamToGameRoomRouteEventList(streamView.streamRef))
					: `${ROUTE_WE_SPIN_GAME_VIEW}/${sessionRef}`;
			} else {
				link = transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_PREVIEW}/${streamView.streamRef}`);
			}
			e.preventDefault();
			e.stopPropagation();
			dispatch(setCurrentStreamView(streamView));
			navigate(link);
		},
		[navigate, streamView, isOnline, sessionRef, transformPath, dispatch]
	);

	const onClickStreamerProfile = useCallback(
		e => {
			const link = transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_STREAMER}/${streamView.streamRef}`);
			e.preventDefault();
			e.stopPropagation();
			dispatch(setCurrentStreamView(streamView));
			navigate(link);
		},
		[navigate, streamView, transformPath, dispatch]
	);

	const boostTypes = new Set();
	streamViews.map(sv => sv.boost && boostTypes.add(getTagText(sv.boost, locale)));
	return (
		<PositionedFigure
			className="u-relative u-m0 item--large-we-spin u-pb1"
			data-at={WE_SPIN_SESSION_CARD_DATA_AT}
			order={cssOrder}
		>
			<VerticalCard
				cardMediaImageComponent={
					<WeSpinSessionCardImage
						onClickLink={onClickLink}
						isOnline={isOnline}
						boostTypes={boostTypes}
						streamView={streamView}
						session={session}
					/>
				}
				cardHeaderTitle={
					<div className="u-flex">
						<H3
							mt={2}
							mb={3}
							className="lobby-card__streamer-name u-cursor-hand"
							onClick={onClickStreamerProfile}
							data-at={WE_SPIN_SESSION_CARD_STREAMER_NAME_DATA_AT}
						>
							{streamView.streamerName}
						</H3>
						<WeSpinFlag language={streamView.language} />
					</div>
				}
				cardHeaderCaption={
					<Caption data-at={WE_SPIN_SESSION_CARD_COUNTDOWN_DATA_AT}>
						<StartTime
							state={
								sessionState === SESSION_ONLINE
									? RUNNING
									: sessionState === SESSION_OFFLINE
									? REGISTRATION
									: ENDED
							}
							start={estimatedStartTime}
							joined={joined}
							isOnline={isOnline}
						/>
					</Caption>
				}
				cardBodyContent={
					<Button
						onClick={onClickLink}
						balanceCapMet={balanceCapMet}
						buyInAmount={currencyFormatter.format(buyInAmount, streamView.currencyCode, locale)}
						isJoined={joined}
						sessionState={sessionState}
						translations={translations}
						noParticipants={noParticipants}
						dataAt={WE_SPIN_SESSION_CARD_PRIMARY_BTN_DATA_AT}
						playerCapMet={playerCapMet}
						isFreeWeSpin={isFreeWeSpin}
						streamView={streamView}
						isStreamerOnline={isOnline}
					/>
				}
			/>
		</PositionedFigure>
	);
};

const propsEqual = (prevProps, nextProps) => {
	return (
		JSON.stringify(prevProps.session) === JSON.stringify(nextProps.session) &&
		JSON.stringify(prevProps.translations) === JSON.stringify(nextProps.translations) &&
		prevProps.cssOrder === nextProps.cssOrder
	);
};
export default memo(WeSpinSessionCard, propsEqual);
